/**
 * GateReportExportButton — download the gate report as a human-readable
 * DOCX or PDF. Calls GET /api/v1/jobs/{jobId}/report/export?format=docx|pdf.
 * Mirrors AuditExportButton's blob-download + accessibility pattern.
 */
import { useState } from 'react';
import { FileText, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

type ExportFormat = 'pdf' | 'docx';

interface GateReportExportButtonProps {
  jobId: string;
  className?: string;
}

async function triggerExport(jobId: string, format: ExportFormat): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const res = await fetch(
    `${API_BASE}/api/v1/jobs/${jobId}/report/export?format=${format}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );

  if (res.status === 202) {
    throw new Error('Report is not ready yet — the job is still processing.');
  }
  if (!res.ok) {
    throw new Error('Export failed. Please try again.');
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `gate-report-${jobId.slice(0, 8)}.${format}`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success(`Report ready: ${filename}`);
}

export function GateReportExportButton({ jobId, className }: GateReportExportButtonProps) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleExport = async (format: ExportFormat) => {
    setOpen(false);
    setGenerating(true);
    try {
      await triggerExport(jobId, format);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={cn('relative inline-flex', className)}>
      <Button
        variant="outline"
        size="sm"
        disabled={generating}
        onClick={() => { setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Create a document from the report"
        onKeyDown={handleKeyDown}
      >
        {generating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            Generating…
          </>
        ) : (
          <>
            <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
            Create document
            <ChevronDown className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
          </>
        )}
      </Button>

      {open && !generating && (
        <div
          role="menu"
          aria-label="Report export format options"
          className="absolute right-0 top-full mt-1 z-10 min-w-[160px] rounded-md border border-border bg-background shadow-md"
          onKeyDown={handleKeyDown}
        >
          <button
            role="menuitem"
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            onClick={() => void handleExport('pdf')}
          >
            Export as PDF
          </button>
          <button
            role="menuitem"
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            onClick={() => void handleExport('docx')}
          >
            Export as Word (DOCX)
          </button>
        </div>
      )}
    </div>
  );
}
